import React, { Fragment, useMemo, useRef, useState } from 'react';

import ArchiveHero from 'components/ArchiveHero';
import CmsModules from 'libs/wordpress/content/CmsModules';
import FilterContainer from 'containers/FilterContainer';
import HeadingAndNumber from 'components/ArchiveHero/HeadingAndNumber';
import MaxWidthWrapper from 'components/wrappers/MaxWidthWrapper';
import ProductListing from 'components/products/ProductListing';
import PropTypes from 'prop-types';
import SubCategoryList from 'components/SubCategoryList';
import { above } from 'utils/mediaqueries';
import colors from 'config/theme/colors';
import getFilterConfig from 'config/filters';
import getSortConfig from 'config/sorts';
import { inServer } from 'config/constants';
import { scrollTo } from 'utils/scrollFunctions';
import styled from 'libs/styled';
import transformFlexibleBackground from 'libs/wordpress/utils/transformFlexibleBackground';
import useAboveBreakpoint from 'hooks/useAboveBreakpoint';
import useCalculatePagesize from 'hooks/useCalculatePagesize';
import useSaveResponse from 'hooks/useSaveResponse';
import { useTranslation } from 'react-i18next';

// Export so that we can use the same margins on the search product listning
export const ListingWrapper = styled('div')`
    margin-bottom: 56px;

    ${above.lg} {
        margin-bottom: 112px;
    }
`;

const Div = styled('div')``;

const Category = ({ data }) => {
    const { t } = useTranslation();

    const {
        breadcrumbs,
        children,
        hero,
        permalink,
        page_content: pageContent,
        centra_relationship: categoryId,
        children: rawSubCategories,
        siblings: rawSiblingCategories,
        ancestors: parentCategories,
        sub_category_overwrite: subCategoryOverwrite,
    } = data;

    const { heading, paragraph, background_media_flexible: backgroundFlexible } = hero.data || {};

    const { use_overwrite: useOverwrite, selected_sub_categories: selectedSubCategories } = subCategoryOverwrite || {};

    const [numberOfProducts, setNumberOfProducts] = useState(null);

    // Transfrom background data
    const isAboveMd = useAboveBreakpoint('md');
    const transformedBackground = transformFlexibleBackground(backgroundFlexible);
    const mobileBackground = transformedBackground.mobile;
    const desktopBackground = transformedBackground.desktop;

    const pageSize = useCalculatePagesize();

    // Save response from container
    const [savedResponse, saveResponse] = useSaveResponse();

    // Scroll to products
    const productsRef = useRef(null);
    const scrollToProducts = () => {
        productsRef.current &&
            scrollTo({
                top: !inServer ? productsRef.current.offsetTop : 0,
            });
    };

    // Setup logic for sub-category links
    const subCategories = useOverwrite
        ? selectedSubCategories
            ? selectedSubCategories.map(cat => ({
                  title: cat.link.text,
                  to: cat.link.url,
              }))
            : []
        : rawSubCategories?.map(cat => ({
              title: cat.title,
              to: cat.permalink,
          }));

    const siblingCategories = useOverwrite
        ? []
        : rawSiblingCategories?.map(cat => ({
              title: cat.title,
              to: cat.permalink,
              current: cat.permalink === permalink,
          }));

    const mainCategory = {
        title: t('category_page.see_all'),
        to: subCategories.length > 0 ? permalink : parentCategories[parentCategories?.length - 1]?.permalink,
        current: subCategories.length > 0,
    };

    const subLinks = subCategories.length > 0 ? subCategories : siblingCategories;
    const subCategoryItems = [mainCategory, ...subLinks];
    const hasSubCategoryItems = subCategoryItems?.length > 1;

    const filterConfig = getFilterConfig(t);
    const sortConfig = getSortConfig(t);

    return useMemo(
        () => (
            <Fragment>
                <ArchiveHero
                    scrollIcon
                    background={isAboveMd ? desktopBackground : mobileBackground}
                    backgroundRatio={[3 / 4, 16 / 9]}
                    breadcrumbs={breadcrumbs}
                    heroDescription={paragraph}
                    heroTitle={heading}
                    numberOfItems={numberOfProducts}
                    shortCutText={t('category_page.show_products')}
                    shortCutType="button"
                    onClick={scrollToProducts}
                />

                {hasSubCategoryItems && (
                    <SubCategoryList
                        ref={productsRef}
                        borderBottom={`1px solid ${colors.grey4}`}
                        items={subCategoryItems}
                    />
                )}

                {pageContent && (
                    <Fragment>
                        <CmsModules isProductCategory data={pageContent} />
                        <Div borderTop={`1px solid ${colors.grey4}`} mt="-1px" py={['32px', null, '38px']}>
                            <MaxWidthWrapper includeContentMargins>
                                <HeadingAndNumber
                                    numberOfItems={numberOfProducts}
                                    title={t('category_page.products')}
                                />
                            </MaxWidthWrapper>
                        </Div>
                        {hasSubCategoryItems && (
                            <SubCategoryList
                                borderBottom={`1px solid ${colors.grey4}`}
                                borderTop={`1px solid ${colors.grey4}`}
                                items={subCategoryItems}
                            />
                        )}
                    </Fragment>
                )}

                <ListingWrapper ref={hasSubCategoryItems ? null : productsRef}>
                    <FilterContainer
                        pageSize={pageSize}
                        config={filterConfig}
                        sorts={sortConfig}
                        categories={[parseInt(categoryId, 10)]}
                        render={({ response }) => {
                            saveResponse(response);
                            setNumberOfProducts(savedResponse.current.hitsCount || 0);

                            return (
                                <ProductListing
                                    response={savedResponse.current}
                                    noResultText={t('category_page.no_products_found')}
                                />
                            );
                        }}
                    />
                </ListingWrapper>
            </Fragment>
        ),
        [
            breadcrumbs,
            isAboveMd,
            categoryId,
            children,
            permalink,
            heading,
            savedResponse,
            numberOfProducts,
            pageContent,
            subCategoryItems,
        ]
    );
};

// CMS data
Category.propTypes = {
    data: PropTypes.any.isRequired,
};

export default Category;
